import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import ObfuscatedEmail, { emailMailto, useEmailAddress } from "@/components/ObfuscatedEmail";

const ContactPreview = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const emailAddress = useEmailAddress();

  return (
    <section ref={ref} className="section-padding bg-background">
      <div className="container-custom mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="space-y-6"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
              Get Your <span className="text-accent">Free Estimate</span> Today
            </h2>
            <p className="text-muted-foreground text-lg">
              Ready to transform your property? Contact us for a free, no-obligation estimate on any of our services.
            </p>

            <div className="space-y-4">
              <a
                href="tel:+14794649398"
               
                className="flex items-center gap-4 p-4 bg-card rounded-lg border border-border hover:border-primary/50 transition-colors group"
              >
                <div className="w-12 h-12 shrink-0 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Phone className="h-6 w-6 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-semibold text-foreground break-words">479-464-9398</p>
                </div>
              </a>

              <a
                href={emailMailto(emailAddress)}
                className="flex items-center gap-4 p-4 bg-card rounded-lg border border-border hover:border-primary/50 transition-colors group"
              >
                <div className="w-12 h-12 shrink-0 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-semibold text-foreground break-all"><ObfuscatedEmail /></p>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 bg-card rounded-lg border border-border">
                <div className="w-12 h-12 shrink-0 bg-primary/10 rounded-full flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-muted-foreground">Location</p>
                  <p className="font-semibold text-foreground break-words">Hindsville, AR 72738</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-card rounded-lg border border-border">
                <div className="w-12 h-12 shrink-0 bg-primary/10 rounded-full flex items-center justify-center">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-muted-foreground">Hours</p>
                  <p className="font-semibold text-foreground break-words">Monday to Friday 8am–5pm, Weekends & Holidays Closed</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="bg-primary-deep p-6 sm:p-8 md:p-12 rounded-lg text-center"
          >

            <h3 className="font-heading text-2xl md:text-3xl font-bold text-primary-foreground mb-4">
              Ready to Get Started?
            </h3>
            <p className="text-primary-foreground mb-8">
              Fill out our quick form for a free estimate, or give us a call anytime!
            </p>
            <div className="flex flex-col gap-4">
              <Link to="/contact">
                <Button variant="hero" size="lg" className="w-full group">
                  Request a Free Estimate
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href="tel:+14794649398">
                <Button variant="heroOutline" size="lg" className="w-full">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactPreview;
